import { useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) return err.response.data.detail;
  return fallback || 'An error occurred';
};

const PARSE_LINES = [
  { text: 'Reading your story...', icon: 'scan', delay: 0 },
  { text: 'Mapping your qualifications to global standards', icon: 'edu', delay: 600 },
  { text: 'Matching your experience across 20+ countries', icon: 'globe', delay: 1400 },
  { text: 'Evaluating PR & Work Permit pathways', icon: 'path', delay: 2200 },
  { text: 'Carving your immigration roadmap', icon: 'map', delay: 3000 },
  { text: 'Preparing your personalized assessment', icon: 'spark', delay: 3800 },
];

// Flow steps: 1=Upload, 2=Register, 3=Parsing, 4=Questions, 5=Results
export const useUploadFlow = () => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState({ full_name: '', email: '', phone: '', nationality: '', contact_consent: false });
  const [uploadData, setUploadData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [parseLines, setParseLines] = useState([]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError('');
      setStep(2);
    }
  }, []);

  const goBackToUpload = useCallback(() => setStep(1), []);

  const parseCVWithTerminal = async (uploadId) => {
    setParseLines([]);
    PARSE_LINES.forEach(({ text, icon, delay }) => {
      setTimeout(() => setParseLines(prev => [...prev, { text, icon }]), delay);
    });

    try {
      try {
        await axios.post(`${API}/parse/cv?upload_id=${uploadId}`);
      } catch {
        await axios.post(`${API}/parse/cv-mock?upload_id=${uploadId}`);
      }

      setParseLines(prev => [...prev, { text: 'Your profile is ready', icon: 'done' }]);

      const qRes = await axios.get(`${API}/questionnaire/questions/${uploadId}`);
      setQuestions(qRes.data.questions || []);

      setTimeout(() => {
        setStep(4);
        setLoading(false);
      }, 1200);
    } catch {
      setParseLines(prev => [...prev, { text: 'Something went wrong. Please try again.', icon: 'error' }]);
      setError('CV parsing failed. Please try again.');
      setLoading(false);
    }
  };

  const registerAndParse = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const regRes = await axios.post(`${API}/users/register`, userData);
      const uid = regRes.data.user_id;
      setUserId(uid);

      const formData = new FormData();
      formData.append('file', file);
      const upRes = await axios.post(`${API}/upload/cv?user_id=${uid}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadData(upRes.data);
      setStep(3);

      await parseCVWithTerminal(upRes.data.upload_id);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to register'));
      setLoading(false);
    }
  };

  const submitQuestionnaire = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/questionnaire/submit/${uploadData.upload_id}`, { answers });
      const eligRes = await axios.post(`${API}/eligibility/calculate-enhanced?upload_id=${uploadData.upload_id}&user_id=${userId}`);
      setAssessment(eligRes.data);
      setStep(5);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to calculate eligibility'));
    } finally {
      setLoading(false);
    }
  };

  return {
    step, file, userData, setUserData, questions, answers, setAnswers,
    assessment, loading, error, parseLines,
    onDrop, goBackToUpload, registerAndParse, submitQuestionnaire,
  };
};
